import {
  Button,
  Card,
  Empty,
  Form,
  Row,
  Col,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
  message,
  Input,
  TableProps,
} from "antd";
import { TaskPrioritySchema } from "../types/task";
import type {
  CreateEvaluationInput,
  Task,
  TaskEvaluation,
} from "../types/task";
import { EditableCell, EditableCellProps } from "./table/cell/EditableCellTask";
import {
  ExclamationCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState, useMemo } from "react";
import {
  useCanCreate,
  useCanEvaluate,
  useCanView,
  useCanViewEvaluations,
} from "./utils";
import {
  useCreateEvaluation,
  useEmployeeEvaluations,
  useEmployeeTasks,
  useEmployeeTasksPermissions,
  useEvaluationOptions,
} from "../hooks/useTasks";
import { AxiosEmployeeAPI } from "employees/Api";
import { EvaluationHistory } from "./evaluation/EvaluationHistory";
import { priorityNames } from "./table/columns/index";
// @ts-ignore
import { useParams, useHistory, useLocation } from "react-router-dom";
import { QueryKey, useQuery } from "react-query";
import { useSendHeightToParent } from "../hooks/useSendHeightToParent";
import {
  PeopleCardButton,
  PeopleCardTag,
  StyledPagination,
} from "../../tmcard/styles";
import "./styles/styles.css";
import { tmCardPurple } from "../../tmcard/colors";

const { Text } = Typography;

// Types for filters and sorting
interface TaskFilters {
  search?: string;
  priority?: string[];
  evaluation?: string[];
}

type TaskSortField = "priority" | "evaluation" | "-priority" | "-evaluation";

interface EmployeeTasksProps {
  employeeId: number;
  positionName: string;
  positionId?: number;
  canEdit?: boolean;
}

const useTranslation = () => {
  return { t: (text: any) => text };
};

interface EditableRowProps {
  index: number;
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <tr {...props} />
    </Form>
  );
};

const PAGE_SIZE = 10;

export const EmployeePositionTasks: React.FC<{
  employeeId?: number | string;
}> = ({ employeeId }) => {
  const { employee_uuid: employeeUUID } = useParams();
  const [positionTasksTMCardRef, setPositionTasksTMCardRef] =
    useState<HTMLDivElement | null>(null);
  useSendHeightToParent(
    { current: positionTasksTMCardRef },
    "positionTasksIFrameResize",
  );

  useEffect(() => {
    if (!!employeeUUID) {
      document.body.style.backgroundColor = "var(--light-grey-background)";
    }
  }, [employeeUUID]);

  const { data, isFetching, isError, refetch } = useQuery(
    ["employeeForTasks", employeeUUID, employeeId] as QueryKey,
    () => {
      if (employeeUUID)
        return AxiosEmployeeAPI.getByUUID(
          { employee_uuid: employeeUUID, expand: "_extra" },
          { signal: null },
        );
      if (employeeId)
        return AxiosEmployeeAPI.getById(
          { employee_id: employeeId, expand: "_extra" },
          { signal: null },
        );
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      staleTime: 60 * 60 * 1000,
      enabled: !!employeeId || !!employeeUUID,
      onError: (error) => console.log("Error ao buscar o funcionário", error),
    },
  );
  const { isFetching: isFetchingPerms } = useEmployeeTasksPermissions(data?.id);
  const canEdit = useCanCreate(data?.id);
  const canView = useCanView(data?.id);

  if (isFetching)
    return (
      <div ref={setPositionTasksTMCardRef}>
        <Spin delay={300} spinning>
          <div
            style={{
              textAlign: "center",
              width: "100%",
              height: "25px",
              margin: "auto",
            }}
          />
        </Spin>
      </div>
    );
  if (isError)
    return (
      <div ref={setPositionTasksTMCardRef}>
        <Result
          status="404"
          title="Não foi possível encontrar o funcionário"
          subTitle="Por favor, tente novamente."
          extra={
            <Button type="primary" onClick={() => refetch()}>
              Retentar
            </Button>
          }
        />
      </div>
    );
  if (!isFetchingPerms && data && !canView)
    return (
      <div ref={setPositionTasksTMCardRef}>
        <Result
          status="403"
          title="Você não tem permissão para acessar este funcionário"
          subTitle="Se você for um BP do funcionário, entre em contato com o RH."
        />
      </div>
    );

  if (data)
    return (
      <div style={{ backgroundColor: "#fafafa" }}>
        <EmployeeTasks
          employeeId={data.id}
          positionName={data?.active_position?.position?.name || "Sem cargo"}
          positionId={data?.active_position?.position?.id}
          canEdit={canEdit}
        />
      </div>
    );
  return null;
};

const EmployeeTasks: React.FC<EmployeeTasksProps> = ({
  employeeId,
  positionName,
  positionId,
  canEdit = true,
}) => {
  const [positionTasksTMCardRef, setPositionTasksTMCardRef] =
    useState<HTMLDivElement | null>(null);
  useSendHeightToParent(
    { current: positionTasksTMCardRef },
    "positionTasksIFrameResize",
    // Add 36px to the height to accommodate the Popover and simulate a margin
    36,
  );

  useEffect(() => {
    document.body.style.backgroundColor = "transparent";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.border = "hidden";
  }, []);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  // Parse query params from URL
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  // Helper functions to get/set query params
  const getQueryParam = (key: string): string | null => queryParams.get(key);
  const getQueryParamArray = (key: string): string[] => queryParams.getAll(key);

  const updateQueryParams = (updates: Record<string, any>) => {
    const newParams = new URLSearchParams(location.search);

    Object.keys(updates).forEach((key) => {
      newParams.delete(key);
      const value = updates[key];
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value)) {
          value.forEach((v) => newParams.append(key, v));
        } else {
          newParams.set(key, value.toString());
        }
      }
    });

    history.push({ search: newParams.toString() });
  };

  const counter = React.useRef<number>(0);
  const [confirmClearKeys, setConfirmClearKeys] = useState<string[]>([]);
  const canEvaluate = useCanEvaluate(employeeId);
  const canViewEvaluations = useCanViewEvaluations(employeeId);

  // Get pagination from URL or default to 1
  const currentPage = parseInt(getQueryParam("page") || "1", 10);
  const setCurrentPage = (page: number) => {
    updateQueryParams({ page: page.toString() });
  };

  // Build API filters from URL params
  const apiFilters = useMemo<TaskFilters>(() => {
    const filters: TaskFilters = {};

    const search = getQueryParam("search");
    if (search) filters.search = search;

    const priorities = getQueryParamArray("priority");
    if (priorities.length > 0) filters.priority = priorities;

    const evaluations = getQueryParamArray("evaluation");
    if (evaluations.length > 0) filters.evaluation = evaluations;

    return filters;
  }, [location.search]);

  // Get ordering from URL
  const apiOrdering = getQueryParam("ordering") as TaskSortField | undefined;

  const [inlineEvaluations, setInlineEvaluations] = useState<
    Record<string, string | undefined>
  >({});
  const [dataSource, setDataSource] = useState<(Task | Partial<Task>)[]>([]);

  const handleAdd = () => {
    setCurrentPage(1);
    const newData: Partial<Task> = { id: `new_${counter.current + 1}` };
    counter.current += 1;
    setDataSource([newData, ...dataSource]);
  };

  const handleDeleteNotSavedTask = (task: EditableCellProps["record"]) => {
    setDataSource(
      dataSource.filter((item: Task | Partial<Task>) => item.id !== task.id),
    );
  };

  // Queries - Note: You'll need to update the useEmployeeTasks hook to accept filters and ordering
  const { data: tasksData, isLoading } = useEmployeeTasks(
    employeeId,
    positionId || -1,
    currentPage,
    apiFilters,
    apiOrdering,
  );
  const { data: evaluationOptions } = useEvaluationOptions();
  const { data: evaluations } = useEmployeeEvaluations(
    employeeId,
    canViewEvaluations,
  );

  React.useEffect(() => {
    if (tasksData?.results) {
      setDataSource(tasksData.results);
    }
  }, [tasksData]);

  // Mutations
  const createEvaluation = useCreateEvaluation(employeeId);

  // Get current evaluation for a task
  const getCurrentAndPreviousEvaluations = (templateTaskId: string) => {
    const taskEvaluations =
      evaluations?.filter((e) => e.template_task_id === templateTaskId) || [];
    return taskEvaluations
      .sort(
        (a, b) =>
          new Date(b.evaluated_at).getTime() -
          new Date(a.evaluated_at).getTime(),
      )
      .slice(0, 2);
  };

  // Handle inline evaluation
  const handleInlineEvaluation = async (
    task: Task,
    evaluationOptionId: string | undefined,
  ) => {
    try {
      setInlineEvaluations((prev) => ({
        ...prev,
        [String(task.template_task_id)]: evaluationOptionId,
      }));

      const data: CreateEvaluationInput = {
        template_task_id: task.template_task.id,
        task_id: task.id,
        evaluation_option_id: evaluationOptionId ?? null,
      };
      await createEvaluation.mutateAsync(data);
      setInlineEvaluations((prev) => ({
        ...prev,
        [String(task.template_task_id)]: evaluationOptionId,
      }));
    } catch (error) {
      message.error(t("Erro ao avaliar a tarefa!"));
      console.log(task, error);
    } finally {
      setConfirmClearKeys((oldKeys) => oldKeys.filter((k) => k !== task.id));
    }
  };

  // Handle table change (filters, sorters, pagination)
  const handleTableChange: TableProps<Task>["onChange"] = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    const updates: Record<string, any> = {};

    // Reset page when filters/sort change
    updates.page = "1";

    // Handle filters
    if (filters?.name?.[0]) {
      updates.search = filters.name[0];
    } else {
      updates.search = null;
    }

    if (filters?.priority && filters.priority.length > 0) {
      updates.priority = filters.priority;
    } else {
      updates.priority = null;
    }

    if (filters?.evaluation && filters.evaluation.length > 0) {
      updates.evaluation = filters.evaluation;
    } else {
      updates.evaluation = null;
    }

    // Handle sorting
    if (!Array.isArray(sorter) && sorter.field) {
      const field = sorter.field as string;
      const order = sorter.order;

      if (order) {
        const orderPrefix = order === "descend" ? "-" : "";
        updates.ordering = `${orderPrefix}${field}`;
      } else {
        updates.ordering = null;
      }
    }

    updateQueryParams(updates);
  };

  // Build table filter values from URL params
  const tableFilters = useMemo<Record<string, any>>(
    () => ({
      name: apiFilters.search ? [apiFilters.search] : undefined,
      priority: apiFilters.priority || undefined,
      evaluation: apiFilters.evaluation || undefined,
    }),
    [apiFilters],
  );

  // Build table sorter from URL params
  const tableSorter = useMemo<
    | {
        field: string;
        order: "descend" | "ascend";
      }
    | undefined
  >(() => {
    if (!apiOrdering) return undefined;

    const isDescending = apiOrdering.startsWith("-");
    const field = isDescending ? apiOrdering.substring(1) : apiOrdering;

    return {
      field,
      order: isDescending ? ("descend" as const) : ("ascend" as const),
    };
  }, [apiOrdering]);

  // Table columns with filters and sorters
  const columns = [
    {
      title: t("Tarefa"),
      dataIndex: ["template_task", "name"],
      key: "name",
      width: "40%",
      filteredValue: tableFilters.name,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t("Buscar tarefa")}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={(e) => {
              confirm();
            }}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t("Buscar")}
            </Button>
            <Button
              onClick={() => {
                clearFilters && clearFilters();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              {t("Limpar")}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onCell: (record: Task) => ({
        record,
        dataIndex: ["template_task", "name"],
        title: "",
        employeeId,
        positionId,
      }),
      render: (name: string, record: Task) => (
        <Space>
          <Tooltip
            placement="right"
            title={
              record.source_object_type === "position"
                ? t("Tarefa do Cargo")
                : t("Tarefa Individual")
            }
          >
            {record.source_object_type === "position" ? (
              <TeamOutlined />
            ) : (
              <UserOutlined />
            )}
          </Tooltip>
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: t("Prioridade"),
      dataIndex: "priority",
      key: "priority",
      width: "15%",
      filters: TaskPrioritySchema.options.map((op) => ({
        text: priorityNames[op],
        value: op,
      })),
      filteredValue: tableFilters?.priority as any,
      sorter: true,
      sortOrder:
        tableSorter?.field === "priority" ? tableSorter.order : undefined,
      sortDirections: ["descend", "ascend"] as Array<"descend" | "ascend">,
      onCell: (record: Task) => ({
        record,
        dataIndex: "priority",
        title: t("Prioridade"),
        employeeId,
      }),
      render: (_: any, record: Task) => priorityNames[record.priority],
    },
    ...(canViewEvaluations
      ? [
          {
            title: t("Avaliação"),
            key: "evaluation",
            dataIndex: "evaluation",
            width: "20%",
            filters: evaluationOptions?.map((option) => ({
              text: option.name,
              value: option.id,
            })),
            filteredValue: tableFilters?.evaluation as any,
            sorter: true,
            sortOrder:
              tableSorter?.field === "evaluation"
                ? tableSorter.order
                : undefined,
            sortDirections: ["descend", "ascend"] as Array<
              "descend" | "ascend"
            >,
            onCell: (record: Task) => ({
              record,
              dataIndex: "evaluation",
              title: "",
              employeeId,
            }),
            render: (_: any, record: Task | Partial<Task>) => {
              let evaluationHistory: TaskEvaluation[] = [];
              let currentEval = undefined;
              let selectedValue = undefined;
              if (record?.template_task) {
                evaluationHistory = getCurrentAndPreviousEvaluations(
                  record.template_task.id,
                );
                currentEval = evaluationHistory?.[0];
                selectedValue =
                  inlineEvaluations[record.template_task.id] ||
                  currentEval?.evaluation_option?.id;
              }
              return (
                <EvaluationHistory
                  currentEval={currentEval}
                  evaluationHistory={evaluationHistory}
                >
                  {canEvaluate && (
                    <Popconfirm
                      placement="top"
                      open={
                        !!record?.id && confirmClearKeys.includes(record?.id)
                      }
                      title={t("Tem certeza que deseja apagar essa avaliação?")}
                      onConfirm={async () => {
                        handleInlineEvaluation(record as Task, undefined);
                      }}
                      onCancel={() =>
                        setConfirmClearKeys((oldKeys) =>
                          oldKeys.filter((k) => k !== record.id),
                        )
                      }
                      okText={t("Sim")}
                      cancelText={t("Cancelar")}
                      icon={<ExclamationCircleOutlined />}
                    >
                      <Select
                        style={{ width: "100%", minWidth: 150 }}
                        placeholder={t("Selecione uma avaliação")}
                        value={selectedValue}
                        onClear={() => {
                          if (record && record?.id !== undefined) {
                            setConfirmClearKeys((oldKeys) => [
                              ...oldKeys,
                              record.id!,
                            ]);
                          }
                        }}
                        onChange={(value) =>
                          value &&
                          record?.id &&
                          handleInlineEvaluation(record as Task, value)
                        }
                        loading={createEvaluation.isLoading}
                        allowClear
                        options={evaluationOptions?.map((option) => ({
                          key: option.id,
                          value: option.id,
                          label: (
                            <PeopleCardTag
                              textColor={option.color_text}
                              color={option.color_tag}
                            >
                              {option.name}
                            </PeopleCardTag>
                          ),
                        }))}
                      />
                    </Popconfirm>
                  )}
                  {!canEvaluate &&
                    (!!currentEval && currentEval.evaluation_option ? (
                      <PeopleCardTag
                        color={currentEval?.evaluation_option.color_tag}
                        textColor={currentEval?.evaluation_option.color_text}
                      >
                        {currentEval?.evaluation_option.name}
                      </PeopleCardTag>
                    ) : (
                      <PeopleCardTag style={{ width: 24, textAlign: "center" }}>
                        -
                      </PeopleCardTag>
                    ))}
                </EvaluationHistory>
              );
            },
          },
        ]
      : []),
    ...(canEdit
      ? [
          {
            title: t("Ações"),
            key: "actions",
            width: "5%",
            onCell: (record: Task) => ({
              record,
              dataIndex: "actions",
              title: "",
              handleDeleteNotSavedTask,
              employeeId,
            }),
          },
        ]
      : []),
  ];

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const tasksCount = tasksData?.count || 0;

  const renderAddTaskButton = () =>
    canEdit && (
      <PeopleCardButton
        icon={<PlusOutlined style={{ fontSize: "14px" }} />}
        onClick={handleAdd}
      >
        {t("Adicionar tarefas")}
      </PeopleCardButton>
    );

  const isFiltered = Object.values(apiFilters).some(
    (filter) =>
      filter !== undefined && (!Array.isArray(filter) || filter.length > 0),
  );

  const shouldShowEmpty =
    !isLoading &&
    tasksData?.results.length === 0 &&
    dataSource.length === 0 &&
    !isFiltered;

  if (shouldShowEmpty) {
    return (
      <div ref={setPositionTasksTMCardRef}>
        <Card style={{ borderRadius: 8 }} bodyStyle={{ paddingBlock: "40px" }}>
          <Space
            direction="vertical"
            size="middle"
            align="center"
            style={{ width: "100%" }}
          >
            <Empty description={t("Não há tarefas da função cadastradas")} />
            {renderAddTaskButton()}
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div ref={setPositionTasksTMCardRef}>
      <Card style={{ borderRadius: 8, border: "none" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              {tasksCount !== 0 ? (
                <>
                  <Text type="secondary" style={{ fontSize: "20px" }}>
                    {positionName}
                  </Text>
                  <br />
                  <Text strong style={{ fontSize: "20px" }}>
                    {tasksCount}{" "}
                    {tasksCount === 1
                      ? t("tarefa adicionada")
                      : t("tarefas adicionadas")}
                  </Text>
                </>
              ) : (
                <Text type="secondary">{positionName}</Text>
              )}
            </div>
            {renderAddTaskButton()}
          </div>

          <Table
            columns={columns}
            dataSource={dataSource as Task[]}
            components={components}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            locale={{
              emptyText: <Empty description={t("Sem tarefas para exibir")} />,
            }}
            className="people-card-table"
          />

          {tasksData && (tasksData.count || 0) > PAGE_SIZE && (
            <Row justify="end" style={{ marginTop: "16px" }}>
              <Col>
                <StyledPagination
                  current={currentPage}
                  total={tasksData.count}
                  pageSize={PAGE_SIZE}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                  style={{ color: tmCardPurple }}
                  size="small"
                />
              </Col>
            </Row>
          )}
        </Space>
      </Card>
    </div>
  );
};

export { EmployeeTasks };
